import { getPointAtAngle, getAngleAndDistance } from '../utils/mindmap/geometry';

/**
 * Represents a position relative to a parent node using polar coordinates
 * This allows for stable positioning during map navigation and transformations
 */
export class RelativePosition {
  /**
   * Create a relative position
   * @param {number} angle - Angle in degrees (0 = right, 90 = down, 180 = left, 270 = up)
   * @param {number} distance - Distance from parent center
   */
  constructor(angle, distance) {
    this.angle = angle;
    this.distance = distance;
  }

  /**
   * Get absolute coordinates from parent position
   * @param {number} parentX - Parent X coordinate
   * @param {number} parentY - Parent Y coordinate
   * @returns {{x: number, y: number}} Absolute coordinates
   */
  getPointFrom(parentX, parentY) {
    return getPointAtAngle(parentX, parentY, this.angle, this.distance);
  }

  /**
   * Create RelativePosition from absolute coordinates
   * @param {number} parentX - Parent X coordinate
   * @param {number} parentY - Parent Y coordinate
   * @param {number} childX - Child X coordinate
   * @param {number} childY - Child Y coordinate
   * @returns {RelativePosition} New RelativePosition instance
   */
  static fromPoint(parentX, parentY, childX, childY) {
    const { angle } = getAngleAndDistance(parentX, parentY, childX, childY);
    const distance = Math.hypot(childX - parentX, childY - parentY);
    return new RelativePosition(angle, distance);
  }

  /**
   * Create RelativePosition from angle and distance
   * @param {number} angle - Angle in degrees
   * @param {number} distance - Distance from parent
   * @returns {RelativePosition} New RelativePosition instance
   */
  static fromAngleAndDistance(angle, distance) {
    return new RelativePosition(angle, distance);
  }

  /**
   * Check if two RelativePositions are equal
   * @param {RelativePosition} other - Other position to compare
   * @returns {boolean} True if positions are equal
   */
  equals(other) {
    return this.angle === other.angle && this.distance === other.distance;
  }

  /**
   * Create a copy of this RelativePosition
   * @returns {RelativePosition} New RelativePosition instance
   */
  clone() {
    return new RelativePosition(this.angle, this.distance);
  }

  /**
   * Serialize to plain object
   * @returns {{angle: number, distance: number}}
   */
  toJSON() {
    return {
      angle: this.angle,
      distance: this.distance
    };
  }

  /**
   * Create RelativePosition from serialized data
   * @param {{angle: number, distance: number}} data - Serialized data
   * @returns {RelativePosition} New RelativePosition instance
   */
  static fromJSON(data) {
    return new RelativePosition(data.angle, data.distance);
  }
}